import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaBrain, FaSpinner } from 'react-icons/fa';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiabilityTracker = ({ clientId }) => {
  const [liabilities, setLiabilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [formData, setFormData] = useState({
    loan_type: 'Home Loan',
    bank_lender: '',
    principal_outstanding: 0,
    interest_rate: 8.5,
    term_years: 15,
    is_insured: false
  });

  useEffect(() => {
    fetchLiabilities();
  }, [clientId]);

  const fetchLiabilities = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/liabilities/${clientId}`);
      setLiabilities(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/liabilities`, { ...formData, client_id: clientId });
      await fetchLiabilities();
      setShowForm(false);
      setFormData({
        loan_type: 'Home Loan',
        bank_lender: '',
        principal_outstanding: 0,
        interest_rate: 8.5,
        term_years: 15,
        is_insured: false
      });
    } catch (error) {
      alert('Failed to add liability');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this loan?')) {
      try {
        await axios.delete(`${API}/liabilities/${id}`);
        await fetchLiabilities();
      } catch (error) {
        alert('Failed to delete liability');
      }
    }
  };

  const formatCurrency = (value) => `₹${Math.round(value).toLocaleString()}`;
  
  const totalUnsecuredDebt = liabilities
    .filter(l => !l.is_insured)
    .reduce((sum, l) => sum + l.principal_outstanding, 0);

  // Get AI Analysis for liabilities
  const getAIAnalysis = async () => {
    setAiLoading(true);
    try {
      const totalDebt = liabilities.reduce((sum, l) => sum + l.principal_outstanding, 0);
      const avgInterestRate = liabilities.length > 0 
        ? liabilities.reduce((sum, l) => sum + l.interest_rate, 0) / liabilities.length 
        : 0;
      
      const liabilityData = {
        total_loans: liabilities.length,
        total_debt: totalDebt,
        total_unsecured_debt: totalUnsecuredDebt,
        average_interest_rate: avgInterestRate,
        loan_types: [...new Set(liabilities.map(l => l.loan_type))],
        insured_count: liabilities.filter(l => l.is_insured).length,
        uninsured_count: liabilities.filter(l => !l.is_insured).length,
        loans_breakdown: liabilities.map(l => ({
          type: l.loan_type,
          lender: l.bank_lender,
          outstanding: l.principal_outstanding,
          rate: l.interest_rate,
          term: l.term_years,
          insured: l.is_insured
        }))
      };
      
      const response = await axios.post(`${API}/net-worth/ai-analysis`, {
        analysis_type: 'liabilities',
        data: liabilityData
      });
      
      // Parse the analysis text into structured sections
      const analysisText = response.data.analysis || response.data.insights || '';
      setAiAnalysis({
        raw: analysisText,
        debt_overview: {
          total_debt: totalDebt,
          unsecured_debt: totalUnsecuredDebt,
          avg_interest: avgInterestRate.toFixed(1),
          insured_loans: liabilities.filter(l => l.is_insured).length,
          uninsured_loans: liabilities.filter(l => !l.is_insured).length
        }
      });
    } catch (error) {
      setAiAnalysis({ error: 'Unable to generate AI analysis at this time. Please try again.' });
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h2 className="text-2xl font-bold text-[#FFD700] mb-2">📋 Current Liabilities & Loans</h2>
        <p className="text-gray-300">Track outstanding debts & insurance</p>
      </div>

      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2D5A8F]">
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Loan Type</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Bank / Lender</th>
                <th className="text-right py-3 px-4 text-gray-400 font-medium">Principal Outstanding</th>
                <th className="text-right py-3 px-4 text-gray-400 font-medium">Rate (%)</th>
                <th className="text-right py-3 px-4 text-gray-400 font-medium">Term (Yrs)</th>
                <th className="text-center py-3 px-4 text-gray-400 font-medium">Insured?</th>
                <th className="text-center py-3 px-4 text-gray-400 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {liabilities.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-400">No loans added yet</td>
                </tr>
              ) : (
                liabilities.map((loan) => (
                  <tr key={loan.id} className="border-b border-[#2D5A8F] hover:bg-[#1E3A5F]">
                    <td className="py-3 px-4 text-white">{loan.loan_type}</td>
                    <td className="py-3 px-4 text-white">{loan.bank_lender}</td>
                    <td className="py-3 px-4 text-right text-white font-medium">{formatCurrency(loan.principal_outstanding)}</td>
                    <td className="py-3 px-4 text-right text-white">{loan.interest_rate}%</td>
                    <td className="py-3 px-4 text-right text-white">{loan.term_years}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${
                        loan.is_insured 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-red-500/20 text-red-300'
                      }`}>
                        {loan.is_insured ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button onClick={() => handleDelete(loan.id)} className="text-red-400 hover:text-red-300">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center space-x-2"
          >
            <FaPlus />
            <span>Add Loan</span>
          </button>

          <div className="bg-red-500/10 border border-red-500 rounded-lg px-6 py-3">
            <p className="text-red-300 font-bold text-lg">
              TOTAL UNSECURED DEBT: {formatCurrency(totalUnsecuredDebt)}
            </p>
          </div>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4 bg-[#0A1929] p-6 rounded-lg border border-[#2D5A8F]">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Loan Type</label>
                <select
                  value={formData.loan_type}
                  onChange={(e) => setFormData({ ...formData, loan_type: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                >
                  <option>Home Loan</option>
                  <option>Car Loan</option>
                  <option>Personal Loan</option>
                  <option>Education Loan</option>
                  <option>Business Loan</option>
                  <option>Credit Card</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Bank / Lender</label>
                <input
                  type="text"
                  value={formData.bank_lender}
                  onChange={(e) => setFormData({ ...formData, bank_lender: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                  placeholder="HDFC, SBI, etc."
                  required
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Principal Outstanding (₹)</label>
                <input
                  type="number"
                  value={formData.principal_outstanding}
                  onChange={(e) => setFormData({ ...formData, principal_outstanding: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Interest Rate (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.interest_rate}
                  onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Term (Years)</label>
                <input
                  type="number"
                  step="0.5"
                  value={formData.term_years}
                  onChange={(e) => setFormData({ ...formData, term_years: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 bg-[#1E3A5F] border border-[#2D5A8F] rounded text-white"
                />
              </div>
              <div className="flex items-center space-x-3 pt-6">
                <input
                  type="checkbox"
                  checked={formData.is_insured}
                  onChange={(e) => setFormData({ ...formData, is_insured: e.target.checked })}
                  className="w-5 h-5"
                />
                <label className="text-gray-300">Loan is Insured</label>
              </div>
            </div>
            <div className="flex space-x-4">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2 bg-[#1E3A5F] text-white rounded">
                Cancel
              </button>
              <button type="submit" className="flex-1 py-2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded">
                Save Loan
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500 rounded-lg">
          <p className="text-gray-300 text-sm">
            <strong>Note:</strong> Uninsured loans are considered "Unsecured Debt" and will be automatically added to the Life Insurance Gap requirement.
          </p>
        </div>
      </div>

      {/* AI-Powered Liability Analysis - Inline Section */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#D4AF37]">
        <div className="flex items-center space-x-3 mb-4">
          <FaBrain className="text-[#FFD700]" size={24} />
          <h3 className="text-xl font-bold text-[#FFD700]">✨ AI-Powered Debt Recommendations</h3>
        </div>

        {aiLoading ? (
          <div className="flex items-center justify-center py-8">
            <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
            <span className="ml-3 text-gray-300">Analyzing your debt profile...</span>
          </div>
        ) : aiAnalysis ? (
          <div className="space-y-4">
            {aiAnalysis.error ? (
              <p className="text-red-400">{aiAnalysis.error}</p>
            ) : (
              <>
                {/* Debt Overview */}
                <div>
                  <h4 className="text-white font-semibold mb-2">📋 Debt Overview</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-[#0A1929] rounded-lg p-3 border border-[#2D5A8F]">
                      <p className="text-gray-400 text-xs">Total Debt</p>
                      <p className="text-white font-bold">{formatCurrency(aiAnalysis.debt_overview?.total_debt)}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded-lg p-3 border border-red-500/50">
                      <p className="text-gray-400 text-xs">Unsecured Debt</p>
                      <p className="text-red-400 font-bold">{formatCurrency(aiAnalysis.debt_overview?.unsecured_debt)}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded-lg p-3 border border-[#2D5A8F]">
                      <p className="text-gray-400 text-xs">Avg Interest</p>
                      <p className="text-white font-bold">{aiAnalysis.debt_overview?.avg_interest}%</p>
                    </div>
                    <div className="bg-[#0A1929] rounded-lg p-3 border border-green-500/50">
                      <p className="text-gray-400 text-xs">Insured Loans</p>
                      <p className="text-green-400 font-bold">{aiAnalysis.debt_overview?.insured_loans}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded-lg p-3 border border-red-500/50">
                      <p className="text-gray-400 text-xs">Uninsured Loans</p>
                      <p className="text-red-400 font-bold">{aiAnalysis.debt_overview?.uninsured_loans}</p>
                    </div>
                  </div>
                </div>

                {/* AI Analysis */}
                <div>
                  <h4 className="text-white font-semibold mb-2">🤖 AI Analysis & Recommendations</h4>
                  <div className="bg-[#0A1929] rounded-lg p-4 border border-[#2D5A8F]">
                    <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{aiAnalysis.raw}</p>
                  </div>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={getAIAnalysis}
                  disabled={liabilities.length === 0}
                  className="px-4 py-2 bg-[#1E3A5F] text-[#FFD700] border border-[#FFD700] rounded-lg hover:bg-[#2D5A8F] transition-all disabled:opacity-50"
                >
                  🔄 Refresh Analysis
                </button>
              </>
            )}
          </div>
        ) : (
          <button
            onClick={getAIAnalysis}
            disabled={liabilities.length === 0}
            className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {liabilities.length === 0 ? 'Add loans to get AI analysis' : 'Get AI Debt Recommendations'}
          </button>
        )}
      </div>
    </div>
  );
};

export default LiabilityTracker;